export const AttributionReportFormats = {
  HTML: 'HTML',
  MD: 'MD',
  PDF: 'PDF',
  CSV: 'CSV',
  TXT: 'TXT',
  SPDX: 'SPDX',
  SPDX_JSON: 'SPDX_JSON',
  CYCLONEDX_JSON: 'CYCLONEDX_JSON',
  CYCLONEDX_XML: 'CYCLONEDX_XML',
} as const;

export type ReportFormat = typeof AttributionReportFormats[keyof typeof AttributionReportFormats];

export const isReportFormat = (format: string): format is ReportFormat =>
  Object.values(AttributionReportFormats).includes(format as ReportFormat);

export const DependencyInfoOptions = {
  Project: 'Project',
  Library: 'Package',
  Authors: 'Authors',
  Description: 'Description',
  ConcludedLicense: 'Concluded License',
  License: 'Declared License',
  CustomTextLicense: 'License Header',
  FullTextLicense: 'Full License Text',
  OtherLicenses: 'Discovered License(s)',
  FilePath: 'File Path',
  Source: 'Package Manager',
  ProjectUrl: 'Package Homepage',
  PackageDownloadUrl: 'Package Download URL',
  DependencyPaths: 'Dependency Paths',
  IssueNotes: 'Issue Resolution Notes',
  Copyrights: 'Copyrights',
  NoticeFiles: 'Notice Files',
  LicenseFileURL: 'License URL',
} as const;

export type DependencyInfoOption = keyof typeof DependencyInfoOptions;

export const allDependencyInfoOptions = Object.keys(DependencyInfoOptions);

export const dependencyInfoOptionsWithDescriptions: Record<DependencyInfoOption, string> = {
  Project: 'Include the project where the dependency was found.', // Release groups only
  Library: 'A third-party software component used in your project.',
  Authors: 'Individuals or organizations credited with creating the package.',
  Description: 'Short summary explaining the package\'s purpose.',
  ConcludedLicense: 'The final license FOSSA determines applies after analyzing declared, discovered, and manual review data.',
  License: 'The license a package author has officially stated in its metadata.',
  CustomTextLicense: 'License information found in the header of a source file.',
  FullTextLicense: 'The complete legal text of a license associated with a dependency.',
  OtherLicenses: 'License FOSSA identified by scanning files when no declared license was found.',
  FilePath: 'The path to the file where this license or component was found.',
  Source: 'Tool or system used to install and manage this package (e.g., npm, Maven, PyPI).',
  ProjectUrl: 'URL pointing to the package\'s main project page or documentation.',
  PackageDownloadUrl: 'Direct link to where the package can be downloaded.',
  DependencyPaths: 'Shows how each dependency is linked back to your project.',
  IssueNotes: 'Comments or clarifications made during the license identification process.',
  Copyrights: 'Ownership statements found in source files or license documents.',
  NoticeFiles: 'Files that summarize license and copyright notices for a package.',
  LicenseFileURL: 'The canonical URL where the full legal text of the license can be accessed.',
};

/*
 * !!!!!!!!!!
 * If you add a new option here, you must also add it to the full report options
 * in `shared/reports/utils.ts`!
 * !!!!!!!!!!
 */
export type AttributionReportIncludeOptions = {
  // CycloneDX only
  includeClosedVulnerabilities?: boolean;
  includeCopyrightList?: boolean;
  includeDeepDependencies?: boolean;
  includeSnippetDependencies?: boolean;
  includeFOSSADependencies?: boolean;
  // All but CSV
  includeDependencySummary?: boolean;
  includeDirectDependencies?: boolean;
  // CSV, TXT, SPDX, SPDX_JSON
  includeFileMatches?: boolean;
  // TXT, HTML, CSV, MD, PDF
  includeHashAndVersionData?: boolean;
  // TXT only
  includeLicenseHeaders?: boolean;
  // All but CSV
  includeLicenseList?: boolean;
  includeLicenseScan?: boolean;
  // All but CSV
  includeNonLicenseCopyrights?: boolean;
  // TXT, HTML, MD, PDF, SPDX
  includeNoticeFiles?: boolean;
  // CycloneDX only
  includeOpenVulnerabilities?: boolean;
  includePackageLabels?: boolean;
  includeProjectLicense?: boolean;
  // CycloneDX only
  includeSupportStatus?: boolean;
  // TXT, HTML, MD, PDF
  includeConcludedLicenses?: boolean;
}
